import { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import { CLIENT_CODES } from "../services/financeCore/clientTaxProfileService";
import { buildAccountsReceivableRows, summarizeAging } from "../services/financeCore/arRegisterService";
import { createReceiptWithAllocations, listReceiptsForClient, reverseReceipt } from "../services/financeCore/receiptAllocationService";
import { RECEIVABLE_STATUSES } from "../services/financeCore/receivablesCalculator";
import { deductionStatus } from "../services/financeCore/deductionLimits";
import { cashExpectedFor, shortfallSummary, validateAdjustment } from "../services/financeCore/receiptShortfall";
import { EXCESS_TOLERANCE, autoFill, excessSources, overdueTargets, poolOf, validateExcessAdjustment } from "../services/financeCore/excessAdjustment";
import { createExcessAdjustment, listCreditAdjustments, reverseExcessAdjustment } from "../services/financeCore/creditAdjustmentService";

const CLIENT_LIST = Object.values(CLIENT_CODES);
// Shared across every Management Finance page (AR, WHT/GST Control) so
// picking a client on one carries over to the others instead of resetting
// to the first client alphabetically every time you switch pages.
const LAST_CLIENT_KEY = "financeSelectedClient";
const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;
const STATUS_CLASS = {
  [RECEIVABLE_STATUSES.RECONCILED]: "success",
  [RECEIVABLE_STATUSES.EXCESS_RECEIVED]: "success",
  [RECEIVABLE_STATUSES.PARTIALLY_PAID]: "warning",
  [RECEIVABLE_STATUSES.OVERDUE]: "danger",
  [RECEIVABLE_STATUSES.UNPAID]: "warning"
};

export default function AccountsReceivable() {
  const [clientCode, setClientCode] = useState(() => {
    const saved = localStorage.getItem(LAST_CLIENT_KEY);
    return CLIENT_LIST.includes(saved) ? saved : CLIENT_LIST[0];
  });
  const [rows, setRows] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [selected, setSelected] = useState({}); // invoiceId -> { whtRecognized, gstWithheldRecognized }
  const [allocationMethod, setAllocationMethod] = useState("PROPORTIONAL");
  const [receiptForm, setReceiptForm] = useState({ receiptDate: new Date().toISOString().slice(0, 10), bankReference: "", receivedAmount: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [shortChoice, setShortChoice] = useState(""); // "" = not decided, BALANCE = rest still due, ADJUST = client adjustment
  const [adjustForm, setAdjustForm] = useState({ amount: "", note: "", accountingPeriod: "" });
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [creditAdjustments, setCreditAdjustments] = useState([]);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustAmounts, setAdjustAmounts] = useState({});
  const [adjustNote, setAdjustNote] = useState("");
  const [adjustDate, setAdjustDate] = useState(new Date().toISOString().slice(0, 10));

  const load = async (code) => {
    setLoading(true); setError(""); setMessage("");
    try {
      const [arRows, receiptRows, adjustmentRows] = await Promise.all([
        buildAccountsReceivableRows(code),
        listReceiptsForClient(code),
        listCreditAdjustments(code)
      ]);
      setCreditAdjustments(adjustmentRows);
      setAdjustAmounts({});
      setRows(arRows.sort((a, b) => (b.invoiceDate || "").localeCompare(a.invoiceDate || "")));
      setReceipts(receiptRows.sort((a, b) => (b.receiptDate || "").localeCompare(a.receiptDate || "")));
      setSelected({});
      setHistoryExpanded(false);
      setHistorySearch("");
    } catch (err) {
      setError(err.message || "Could not load the receivables register.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(clientCode); }, [clientCode]);

  const selectClient = (code) => {
    setClientCode(code);
    localStorage.setItem(LAST_CLIENT_KEY, code);
  };

  const filteredReceipts = useMemo(() => {
    const term = historySearch.trim().toLowerCase();
    if (!term) return receipts;
    return receipts.filter((receipt) => [
      receipt.receiptDate, receipt.bankReference, receipt.notes, receipt.enteredByEmail, receipt.receivedAmount
    ].filter(Boolean).some((field) => String(field).toLowerCase().includes(term)));
  }, [receipts, historySearch]);

  const aging = useMemo(() => summarizeAging(rows), [rows]);
  const outstandingRows = useMemo(() => rows.filter((row) => row.outstanding > 5), [rows]);
  const selectedIds = Object.keys(selected);
  const selectedRows = outstandingRows.filter((row) => selectedIds.includes(row.id));
  const selectedExpectedTotal = selectedRows.reduce((sum, row) => sum + Number(row.totals?.expectedReceipt || 0), 0);
  const selectedManualTotal = selectedRows.reduce((sum, row) => sum + Number(selected[row.id]?.allocatedAmount || 0), 0);
  const manualDifference = Math.round(Number(receiptForm.receivedAmount || 0)) - Math.round(selectedManualTotal);

  // If the client paid less than what these invoices should bring in, the operator
  // must say what happens to the rest before the receipt can be saved.
  const cashExpectedOf = (row) => cashExpectedFor(row.outstanding, selected[row.id]?.whtRecognized, selected[row.id]?.gstWithheldRecognized);
  const shortInfo = selectedRows.length && Number(receiptForm.receivedAmount) > 0
    ? shortfallSummary({ selections: selectedRows.map((row) => ({ cashExpected: cashExpectedOf(row) })), receivedAmount: receiptForm.receivedAmount })
    : { expectedTotal: 0, received: 0, shortfall: 0, isShort: false };

  useEffect(() => {
    if (!shortInfo.isShort) { setShortChoice(""); return; }
    setAdjustForm((current) => (Number(current.amount) > shortInfo.shortfall ? { ...current, amount: String(shortInfo.shortfall) } : current));
  }, [shortInfo.isShort, shortInfo.shortfall]);

  // Excess already sitting on invoices (client overpaid) and the overdue invoices it could clear.
  const sources = useMemo(() => excessSources(rows), [rows]);
  const targets = useMemo(() => overdueTargets(rows), [rows]);
  const pool = poolOf(sources);
  const applyingTotal = Object.values(adjustAmounts).reduce((sum, value) => sum + (Math.round(Number(value)) || 0), 0);
  const adjustmentGroups = useMemo(() => {
    const groups = new Map();
    creditAdjustments.forEach((item) => {
      const group = groups.get(item.groupId) || { groupId: item.groupId, date: item.adjustmentDate, note: item.note, by: item.createdByEmail, total: 0, items: [], reversed: true };
      group.total += Number(item.amount || 0);
      group.items.push(item);
      if (!item.reversed) group.reversed = false;
      groups.set(item.groupId, group);
    });
    return [...groups.values()].sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [creditAdjustments]);
  // A receipt that pays more than the selected invoices expect creates excess; point the operator at the adjustment panel.
  const excessOnThisReceipt = selectedRows.length && Number(receiptForm.receivedAmount) > 0
    ? Math.round(Number(receiptForm.receivedAmount)) - shortInfo.expectedTotal
    : 0;

  const fillOldestFirst = () => setAdjustAmounts(Object.fromEntries(Object.entries(autoFill(pool, targets)).map(([id, value]) => [id, String(value)])));

  async function applyExcess(event) {
    event.preventDefault();
    setError(""); setMessage("");
    const problem = validateExcessAdjustment({ amounts: adjustAmounts, targets, pool, note: adjustNote, date: adjustDate });
    if (problem) { setError(problem); return; }
    if (!window.confirm(`Apply PKR ${applyingTotal.toLocaleString("en-PK")} of ${clientCode}'s excess against ${Object.values(adjustAmounts).filter((v) => Number(v) > 0).length} overdue invoice(s)? This only moves money the client already paid between invoices; profit is unaffected.`)) return;
    setSaving(true);
    try {
      const result = await createExcessAdjustment({ clientCode, amounts: adjustAmounts, note: adjustNote, date: adjustDate });
      setAdjustNote("");
      await load(clientCode);
      setMessage(`PKR ${result.total.toLocaleString("en-PK")} of excess was applied against overdue invoices.`);
    } catch (err) {
      setError(err.message || "The adjustment could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function undoExcess(group) {
    const reason = window.prompt("Reason for reversing this excess adjustment?");
    if (!reason) return;
    try {
      await reverseExcessAdjustment(clientCode, group.groupId, reason);
      await load(clientCode);
      setMessage("The excess adjustment was reversed.");
    } catch (err) {
      setError(err.message || "The adjustment could not be reversed.");
    }
  }

  const chooseShort = (choice) => {
    setShortChoice(choice);
    if (choice === "ADJUST") {
      setAdjustForm((current) => ({ amount: current.amount || String(shortInfo.shortfall), note: current.note, accountingPeriod: current.accountingPeriod || String(receiptForm.receiptDate || "").slice(0, 7) }));
    }
  };

  const toggleSelect = (row) => {
    setSelected((current) => {
      const next = { ...current };
      if (next[row.id]) {
        delete next[row.id];
      } else {
        const whtRecognized = row.totals?.whtAmount || 0;
        const gstWithheldRecognized = row.totals?.gstWithheldAmount || 0;
        next[row.id] = {
          whtRecognized,
          gstWithheldRecognized,
          // Default to "this invoice's gross, less whatever tax is actually
          // recognized" — the common case (client paid in full, no
          // deductions) reconciles with zero typing; Manual mode still lets
          // this be overridden per row.
          allocatedAmount: Math.max(0, Math.round(Number(row.totals?.grossAmount || 0) - whtRecognized - gstWithheldRecognized))
        };
      }
      return next;
    });
  };

  const updateSelection = (rowId, field, value) => {
    setSelected((current) => {
      const existing = current[rowId] || {};
      const next = { ...existing, [field]: value };
      // Removing/editing recognized WHT or GST keeps the allocated amount in
      // sync automatically — e.g. the client didn't deduct WHT this time, so
      // zeroing it back out should top the allocation back up to full gross —
      // without this, Manual mode requires retyping the amount by hand and a
      // stale allocatedAmount silently fails the "allocations must add up to
      // the received amount" check on submit.
      if (field === "whtRecognized" || field === "gstWithheldRecognized") {
        const row = outstandingRows.find((r) => r.id === rowId);
        if (row) {
          const wht = field === "whtRecognized" ? Number(value || 0) : Number(next.whtRecognized || 0);
          const gst = field === "gstWithheldRecognized" ? Number(value || 0) : Number(next.gstWithheldRecognized || 0);
          next.allocatedAmount = Math.max(0, Math.round(Number(row.totals?.grossAmount || 0) - wht - gst));
        }
      }
      return { ...current, [rowId]: next };
    });
  };

  async function saveReceipt(event) {
    event.preventDefault();
    setError(""); setMessage("");
    if (shortInfo.isShort && !shortChoice) {
      setError(`The client paid ${money(shortInfo.shortfall)} less than expected. Choose what to do with the remaining amount before saving.`);
      return;
    }
    const adjusting = shortInfo.isShort && shortChoice === "ADJUST";
    if (adjusting) {
      const problem = validateAdjustment({ ...adjustForm, shortfall: shortInfo.shortfall });
      if (problem) { setError(problem); return; }
      const confirmed = window.confirm(
        `Record this receipt with a client adjustment?\n\n` +
        `Received: ${money(receiptForm.receivedAmount)}\nExpected: ${money(shortInfo.expectedTotal)}\nAdjustment: ${money(adjustForm.amount)}\n\n` +
        `${money(adjustForm.amount)} will be booked as a direct cost of ${clientCode} (Client Adjustment) in ${adjustForm.accountingPeriod}, ` +
        `the invoices will be cleared of that amount, and Monthly P&L and Client Profitability will show it.`
      );
      if (!confirmed) return;
    }
    setSaving(true);
    try {
      const selections = selectedRows.map((row) => ({
        snapshotId: row.id,
        invoiceNumber: row.invoiceNumber,
        expectedReceipt: row.totals?.expectedReceipt || 0,
        cashExpected: cashExpectedOf(row),
        whtRecognized: Number(selected[row.id]?.whtRecognized || 0),
        gstWithheldRecognized: Number(selected[row.id]?.gstWithheldRecognized || 0),
        deductionOverrideReason: selected[row.id]?.deductionOverrideReason || "",
        allocatedAmount: allocationMethod === "MANUAL" ? Number(selected[row.id]?.allocatedAmount || 0) : undefined
      }));
      const result = await createReceiptWithAllocations({
        clientCode,
        receiptDate: receiptForm.receiptDate,
        bankReference: receiptForm.bankReference,
        receivedAmount: receiptForm.receivedAmount,
        notes: receiptForm.notes,
        allocationMethod,
        selections,
        adjustment: adjusting ? adjustForm : null
      });
      const savedMessage =
        `Receipt recorded and allocated to ${selections.length} invoice(s).` +
        (result.adjustmentAmount ? ` ${money(result.adjustmentAmount)} was booked as a client adjustment (direct cost of ${clientCode}).` : "") +
        (shortInfo.isShort && shortChoice === "BALANCE" ? ` ${money(shortInfo.shortfall)} stays outstanding.` : "");
      setReceiptForm((current) => ({ ...current, bankReference: "", receivedAmount: "", notes: "" }));
      setAdjustForm({ amount: "", note: "", accountingPeriod: "" });
      setShortChoice("");
      const overpaid = excessOnThisReceipt > EXCESS_TOLERANCE;
      await load(clientCode);
      if (overpaid) setAdjustOpen(true);
      setMessage(savedMessage + (overpaid ? ` The client paid ${money(excessOnThisReceipt)} more than expected — use "Adjust Excess Against Overdue" below to apply it.` : ""));
    } catch (err) {
      setError(err.message || "The receipt could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function undoReceipt(receipt) {
    const reason = window.prompt(`Reason for reversing receipt ${receipt.bankReference}?`);
    if (!reason) return;
    try {
      await reverseReceipt(receipt.id, reason);
      await load(clientCode);
      setMessage(`Receipt ${receipt.bankReference} was reversed.`);
    } catch (err) {
      setError(err.message || "The receipt could not be reversed.");
    }
  }

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Management Finance</div>
            <h1 className="page-title">Accounts Receivable</h1>
            <p className="page-copy">
              Built from approved invoice snapshots and recorded receipts — nothing here is retyped, and every figure
              traces back to an invoice or a bank receipt.
            </p>
          </div>
          <div className="field" style={{ minWidth: 200 }}>
            <label>Client</label>
            <select className="input" value={clientCode} onChange={(e) => selectClient(e.target.value)}>
              {CLIENT_LIST.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
        </header>

        {(message || error) && <div className={`management-alert ${error ? "error" : "success"}`}>{error || message}</div>}

        <section className="management-metrics">
          {["CURRENT", "1-30", "31-60", "61-90", "90+"].map((bucket) => (
            <article className="metric-card" key={bucket}>
              <div className="metric-label">{bucket}</div>
              <div className="metric-value">{money(aging[bucket])}</div>
            </article>
          ))}
          <article className="metric-card">
            <div className="metric-label">Total Outstanding</div>
            <div className="metric-value">{money(aging.total)}</div>
          </article>
        </section>

        <section className="panel">
          <div className="panel-header"><h2 className="panel-title">Approved Invoices</h2></div>
          {loading ? <div className="panel-body page-copy">Loading…</div> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Select</th><th>Invoice</th><th>Invoice Date</th><th>Due Date</th><th>Gross</th>
                    <th>Cash Received</th><th>WHT Recognized</th><th>GST Withheld</th><th>Client Adjustment</th><th>Excess Moved</th><th>Outstanding</th><th>Aging</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {row.outstanding > 5 && (
                          <input type="checkbox" checked={!!selected[row.id]} onChange={() => toggleSelect(row)} />
                        )}
                      </td>
                      <td>{row.invoiceNumber}</td>
                      <td>{row.invoiceDate}</td>
                      <td>{row.dueDate}</td>
                      <td>{money(row.totals?.grossAmount)}</td>
                      <td>{money(row.allocatedAmount)}</td>
                      <td>{money(row.whtRecognized)}</td>
                      <td>{money(row.gstWithheldRecognized)}</td>
                      <td>{row.otherDeductionsRecognized ? money(row.otherDeductionsRecognized) : "—"}</td>
                      <td>{row.creditAdjustment ? `${row.creditAdjustment > 0 ? "+" : "−"}${money(Math.abs(row.creditAdjustment))}` : "—"}</td>
                      <td><strong>{money(row.outstanding)}</strong></td>
                      <td>{row.agingBucket}</td>
                      <td><span className={`status-pill ${STATUS_CLASS[row.status] || ""}`}>{row.status}</span></td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan={13}>No approved invoices for {clientCode} yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Record a Receipt</h2>
              <p className="page-copy">{selectedRows.length} invoice(s) selected · expected total {money(selectedExpectedTotal)}</p>
              {allocationMethod === "MANUAL" && !!selectedRows.length && (
                <p className="page-copy" style={{ marginTop: 2, color: manualDifference === 0 ? "" : "#c0392b", fontWeight: manualDifference === 0 ? "normal" : 600 }}>
                  Allocated {money(selectedManualTotal)} of {money(receiptForm.receivedAmount || 0)} received
                  {manualDifference !== 0 && ` — ${money(Math.abs(manualDifference))} ${manualDifference > 0 ? "unallocated" : "over-allocated"}`}
                </p>
              )}
            </div>
            <div className="field" style={{ minWidth: 180 }}>
              <label>Allocation method</label>
              <select className="input" value={allocationMethod} onChange={(e) => setAllocationMethod(e.target.value)}>
                <option value="PROPORTIONAL">Proportional</option>
                <option value="MANUAL">Manual</option>
              </select>
            </div>
          </div>
          <form className="panel-body" onSubmit={saveReceipt}>
            <div className="form-grid">
              <div className="field"><label>Receipt Date *</label>
                <input className="input" type="date" value={receiptForm.receiptDate} onChange={(e) => setReceiptForm({ ...receiptForm, receiptDate: e.target.value })} />
              </div>
              <div className="field"><label>Bank Reference *</label>
                <input className="input" value={receiptForm.bankReference} onChange={(e) => setReceiptForm({ ...receiptForm, bankReference: e.target.value })} />
              </div>
              <div className="field"><label>Received Amount (PKR) *</label>
                <input className="input" type="number" min="0.01" step="0.01" value={receiptForm.receivedAmount} onChange={(e) => setReceiptForm({ ...receiptForm, receivedAmount: e.target.value })} />
              </div>
            </div>
            <div className="field"><label>Notes</label>
              <textarea className="textarea" value={receiptForm.notes} onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })} />
            </div>

            {excessOnThisReceipt > EXCESS_TOLERANCE && (
              <div className="management-alert success" style={{ marginTop: 12 }}>
                <strong>The client paid {money(excessOnThisReceipt)} more than expected on these invoices.</strong>
                <div>After saving, it will show as excess. You can then apply it against this client's overdue invoices under "Adjust Excess Against Overdue".</div>
              </div>
            )}

            {shortInfo.isShort && (
              <div className="management-alert error" style={{ marginTop: 12 }}>
                <strong>The client paid {money(shortInfo.shortfall)} less than expected.</strong>
                <div>Expected {money(shortInfo.expectedTotal)} for the selected invoices, received {money(shortInfo.received)}. What should happen to the remaining {money(shortInfo.shortfall)}?</div>
                <div style={{ marginTop: 8 }}>
                  <label style={{ display: "block", marginBottom: 6 }}>
                    <input type="radio" name="short-choice" checked={shortChoice === "BALANCE"} onChange={() => chooseShort("BALANCE")} />{" "}
                    The client will pay the rest later — leave {money(shortInfo.shortfall)} outstanding
                  </label>
                  <label style={{ display: "block" }}>
                    <input type="radio" name="short-choice" checked={shortChoice === "ADJUST"} onChange={() => chooseShort("ADJUST")} />{" "}
                    Client adjustment — accept the shortfall and book it as a direct cost of {clientCode}
                  </label>
                </div>
                {shortChoice === "ADJUST" && (
                  <div className="form-grid" style={{ marginTop: 12 }}>
                    <div className="field"><label>Adjustment amount (PKR) *</label>
                      <input className="input" type="number" min="1" max={shortInfo.shortfall} value={adjustForm.amount} onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })} />
                    </div>
                    <div className="field"><label>Book in month *</label>
                      <input className="input" type="month" value={adjustForm.accountingPeriod} onChange={(e) => setAdjustForm({ ...adjustForm, accountingPeriod: e.target.value })} />
                    </div>
                    <div className="field field-wide"><label>Note — why the client paid less *</label>
                      <textarea className="textarea" value={adjustForm.note} onChange={(e) => setAdjustForm({ ...adjustForm, note: e.target.value })}
                        placeholder="e.g. Client deducted a penalty for late attendance report; agreed with the client's finance team on 20 Oct." />
                    </div>
                    <p className="page-copy field-wide" style={{ margin: 0 }}>
                      Any part of the shortfall you do not adjust stays outstanding. The adjustment reduces {clientCode}'s profit in Monthly P&amp;L and Client Profitability.
                    </p>
                  </div>
                )}
              </div>
            )}

            {!!selectedRows.length && (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      {allocationMethod === "MANUAL" && <th>Allocated Amount</th>}
                      <th>WHT Recognized</th><th>GST Withheld Recognized</th><th>Reason if above the invoice's WHT/GST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.invoiceNumber}</td>
                        {allocationMethod === "MANUAL" && (
                          <td><input className="input" type="number" value={selected[row.id]?.allocatedAmount ?? 0} onChange={(e) => updateSelection(row.id, "allocatedAmount", e.target.value)} /></td>
                        )}
                        <td><input className="input" type="number" value={selected[row.id]?.whtRecognized ?? 0} onChange={(e) => updateSelection(row.id, "whtRecognized", e.target.value)} /></td>
                        <td><input className="input" type="number" value={selected[row.id]?.gstWithheldRecognized ?? 0} onChange={(e) => updateSelection(row.id, "gstWithheldRecognized", e.target.value)} /></td>
                        <td>{(() => {
                          const limits = deductionStatus({
                            expectedWht: row.totals?.whtAmount, expectedGst: row.totals?.gstWithheldAmount,
                            priorWht: row.whtRecognized, priorGst: row.gstWithheldRecognized,
                            wht: selected[row.id]?.whtRecognized, gst: selected[row.id]?.gstWithheldRecognized
                          });
                          if (!limits.exceeds) return <span className="page-copy">Within the invoice</span>;
                          return (
                            <div>
                              <div className="page-copy" style={{ color: "#c0392b" }}>
                                Still allowed: WHT {money(limits.whtRemaining)}, GST withheld {money(limits.gstRemaining)}
                              </div>
                              <input className="input" placeholder="Why is it higher? (required)" value={selected[row.id]?.deductionOverrideReason ?? ""}
                                onChange={(e) => updateSelection(row.id, "deductionOverrideReason", e.target.value)} />
                            </div>
                          );
                        })()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="button-row">
              <button className="btn btn-primary" type="submit" disabled={saving || !selectedRows.length || (shortInfo.isShort && !shortChoice)}>
                {saving ? "Saving…" : "Save Receipt"}
              </button>
            </div>
          </form>
        </section>

        {(sources.length > 0 || adjustmentGroups.length > 0) && (
          <section className="panel">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Adjust Excess Against Overdue</h2>
                <p className="page-copy">
                  {sources.length > 0 ? `${clientCode} has paid ${money(pool)} more than owed on ${sources.length} invoice(s). Apply it against overdue invoices below.` : "No excess is waiting to be applied."}
                </p>
              </div>
              <button className="btn btn-muted" type="button" onClick={() => setAdjustOpen((current) => !current)}>{adjustOpen ? "Hide" : "Show"}</button>
            </div>
            {adjustOpen && (
              <div className="panel-body">
                {sources.length > 0 && (
                  <>
                    <p className="page-copy">Excess on: {sources.map((source) => `${source.invoiceNumber} (${money(source.available)})`).join(", ")}</p>
                    {!targets.length ? <p className="page-copy">There are no overdue invoices to apply it to.</p> : (
                      <form onSubmit={applyExcess}>
                        <div className="table-wrap">
                          <table className="data-table">
                            <thead><tr><th>Overdue Invoice</th><th>Due Date</th><th>Days Overdue</th><th>Still Owed</th><th>Apply (PKR)</th></tr></thead>
                            <tbody>
                              {targets.map((target) => (
                                <tr key={target.id}>
                                  <td>{target.invoiceNumber}</td><td>{target.dueDate}</td><td>{target.daysPastDue}</td><td>{money(target.outstanding)}</td>
                                  <td><input className="input" type="number" min="0" max={target.outstanding} value={adjustAmounts[target.id] ?? ""}
                                    onChange={(e) => setAdjustAmounts({ ...adjustAmounts, [target.id]: e.target.value })} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="page-copy" style={{ color: applyingTotal > pool ? "#c0392b" : "", fontWeight: applyingTotal > pool ? 600 : "normal" }}>
                          Applying {money(applyingTotal)} of {money(pool)} excess — {money(Math.max(0, pool - applyingTotal))} would remain as excess.
                        </p>
                        <div className="form-grid">
                          <div className="field"><label>Adjustment date *</label>
                            <input className="input" type="date" value={adjustDate} onChange={(e) => setAdjustDate(e.target.value)} />
                          </div>
                          <div className="field field-wide"><label>Note *</label>
                            <textarea className="textarea" value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)}
                              placeholder="e.g. Client confirmed by email that the extra payment on 2027-010 is for the overdue July invoice." />
                          </div>
                        </div>
                        <div className="button-row">
                          <button className="btn btn-muted" type="button" onClick={fillOldestFirst}>Fill oldest first</button>
                          <button className="btn btn-primary" type="submit" disabled={saving || !applyingTotal}>Apply Excess</button>
                        </div>
                      </form>
                    )}
                  </>
                )}
                {adjustmentGroups.length > 0 && (
                  <div className="table-wrap" style={{ marginTop: 16 }}>
                    <table className="data-table">
                      <thead><tr><th>Date</th><th>Moved from → to</th><th>Amount</th><th>Note</th><th>Status</th><th></th></tr></thead>
                      <tbody>
                        {adjustmentGroups.map((group) => (
                          <tr key={group.groupId} style={group.reversed ? { opacity: 0.55 } : undefined}>
                            <td>{group.date}</td>
                            <td style={{ fontSize: 12 }}>{group.items.map((item) => `${item.fromInvoiceNumber} → ${item.toInvoiceNumber} (${money(item.amount)})`).join("; ")}</td>
                            <td>{money(group.total)}</td><td>{group.note}</td>
                            <td>{group.reversed ? <span className="status-pill danger">Reversed</span> : <span className="status-pill success">Applied</span>}</td>
                            <td>{!group.reversed && <button className="btn btn-danger" type="button" onClick={() => undoExcess(group)}>Reverse</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {!!receipts.length && (
          <section className="panel">
            <div className="panel-header">
              <h2 className="panel-title">Receipt History ({receipts.length})</h2>
              <button className="btn btn-muted" type="button" onClick={() => setHistoryExpanded((current) => !current)}>
                {historyExpanded ? "Hide" : "Show"}
              </button>
            </div>
            {historyExpanded && (
              <>
                <div className="panel-body" style={{ paddingBottom: 0 }}>
                  <input
                    className="input"
                    placeholder="Search by date, reference, amount, or entered-by…"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                  />
                </div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Date</th><th>Reference</th><th>Amount</th><th>Client Adjustment</th><th>Entered By</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      {filteredReceipts.map((receipt) => (
                        <tr key={receipt.id}>
                          <td>{receipt.receiptDate}</td>
                          <td>{receipt.bankReference}</td>
                          <td>{money(receipt.receivedAmount)}</td>
                          <td>{receipt.adjustmentAmount ? <>{money(receipt.adjustmentAmount)}<div className="page-copy" style={{ fontSize: 12 }}>{receipt.adjustmentNote}</div></> : "—"}</td>
                          <td>{receipt.enteredByEmail || "—"}</td>
                          <td>{receipt.reversed ? <span className="status-pill danger">Reversed</span> : <span className="status-pill success">Active</span>}</td>
                          <td>{!receipt.reversed && <button className="btn btn-danger" type="button" onClick={() => undoReceipt(receipt)}>Reverse</button>}</td>
                        </tr>
                      ))}
                      {!filteredReceipts.length && <tr><td colSpan={7}>No receipts match "{historySearch}".</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
